import React, { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Save, Undo2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { getStatusConfig } from "@/config/status-registry";

interface ReturnDeliveryFormProps {
  initialData: any;
  onCancel: () => void;
}

const CUID_OR_UUID_RE = /^(c[a-z0-9]{20,32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

function isReturnablePhysicalItem(item: any): boolean {
  if (!item || item.isDeleted) return false;
  if (item.isServiceItem) return false;
  const name = String(item.productName || "").trim().toLowerCase();
  if (
    !item.productId &&
    !item.orderDetailId &&
    (item.serviceType === "VAT" ||
      name.includes("vat") ||
      name.includes("thuế giá trị gia tăng") ||
      name.includes("phí vận chuyển"))
  ) {
    return false;
  }
  return true;
}

export default function ReturnDeliveryForm({
  initialData,
  onCancel,
}: ReturnDeliveryFormProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notes, setNotes] = useState("");

  const initialPhysicalItems = useMemo(
    () =>
      (initialData.deliveryItems || [])
        .filter(isReturnablePhysicalItem)
        .map((item: any) => ({
          ...item,
          returnedQty: 0,
          reason: "",
        })),
    [initialData.deliveryItems],
  );

  const [returnItems, setReturnItems] = useState(initialPhysicalItems);
  const statusCfg = getStatusConfig("DeliveryStatus", initialData.status);

  const handleQtyChange = (index: number, val: string) => {
    const num = parseInt(val, 10) || 0;
    const max = Number(returnItems[index].deliveredQty || 0);
    const updated = [...returnItems];
    updated[index].returnedQty = num > max ? max : num < 0 ? 0 : num;
    setReturnItems(updated);
  };

  const handleReasonChange = (index: number, val: string) => {
    const updated = [...returnItems];
    updated[index].reason = val;
    setReturnItems(updated);
  };

  const handleSubmit = async () => {
    const itemsToReturn = returnItems.filter((i: any) => Number(i.returnedQty) > 0);
    if (itemsToReturn.length === 0) {
      toast.error("Vui lòng nhập số lượng trả lại cho ít nhất 1 sản phẩm");
      return;
    }

    try {
      setIsSubmitting(true);
      await trpcClient.deliveries.processReturn.mutate({
        id: initialData.id,
        data: {
          notes,
          items: itemsToReturn.map((i: any) => ({
            deliveryItemId: i.id,
            productId: i.productId ?? null,
            productName: i.productName,
            returnedQty: Number(i.returnedQty),
            reason: i.reason,
          })),
        },
      });
      toast.success("Đã ghi nhận trả hàng và nhập kho thành công");
      await queryClient.invalidateQueries({
        queryKey: ["delivery", initialData.id],
      });
      await queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory"] });
      onCancel();
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Lỗi khi xử lý trả hàng");
    } finally {
      setIsSubmitting(false);
    }
  };

  const customerDisplayName =
    initialData.customer?.fullName || initialData.customer?.name || "—";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center text-indigo-700">
            <Undo2 className="mr-2 h-5 w-5" />
            Chi tiết trả hàng
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-slate-500 text-xs uppercase font-semibold">
                Khách hàng
              </Label>
              <div className="font-medium text-slate-800">
                {customerDisplayName}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-500 text-xs uppercase font-semibold">
                Trạng thái phiếu
              </Label>
              <div>
                <Badge variant="outline" className={statusCfg.color}>
                  {statusCfg.label}
                </Badge>
              </div>
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label htmlFor="notes">Ghi chú trả hàng</Label>
              <Input
                id="notes"
                placeholder="Lý do trả hàng chung..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg text-slate-800">
            Sản phẩm trả lại
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Sản phẩm</TableHead>
                  <TableHead className="text-right w-24">SL Đã Giao</TableHead>
                  <TableHead className="w-32">SL Trả Lại</TableHead>
                  <TableHead>Lý do / Ghi chú</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returnItems.map((item: any, index: number) => {
                  const displayProductCode =
                    item.product?.productCode ||
                    (item.productId && !CUID_OR_UUID_RE.test(String(item.productId))
                      ? item.productId
                      : null) ||
                    item.soNumber ||
                    null;

                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        {item.productName}
                        {displayProductCode && (
                          <div className="text-xs text-slate-500 font-mono">
                            Mã: {displayProductCode}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-slate-600">
                        {item.deliveredQty} {item.unit}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          max={item.deliveredQty}
                          value={item.returnedQty || ""}
                          onChange={(e) => handleQtyChange(index, e.target.value)}
                          className="w-24 text-right"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="Lý do..."
                          value={item.reason}
                          onChange={(e) =>
                            handleReasonChange(index, e.target.value)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between border-t border-slate-200 pt-6">
        <div className="flex items-center text-amber-600 text-sm">
          <AlertCircle className="h-4 w-4 mr-1" />
          Trả hàng sẽ tự động tạo Phiếu Nhập Kho (Hàng Trả) và cộng lại Tồn kho
          thành phẩm.
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Hủy bỏ
          </Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Đang xử lý..." : "Xác nhận trả hàng"}
            {!isSubmitting && <Save className="ml-2 h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
